import { AccountSelect } from "@/components/form-fields/account-select";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { z } from "zod";

const securityOwnerFormSchema = z.object({
  securityId: z.string().uuid(),
  ownerType: z.enum(["account", "contact"]),
  ownerAccountId: z.string().uuid().optional().nullable(),
  ownerContactId: z.string().uuid().optional().nullable(),
  ownershipBasis: z.enum(["sole", "joint_tenants", "tenants_in_common"]),
  ownershipPercentage: z.string().min(1, "Ownership percentage is required"),
  isRegisteredProprietor: z.boolean().default(true),
  notes: z.string().optional().nullable(),
}).refine(
  (data) => {
    // XOR validation: exactly one of ownerAccountId or ownerContactId must be set
    const hasAccount = !!data.ownerAccountId;
    const hasContact = !!data.ownerContactId;
    return (hasAccount && !hasContact) || (!hasAccount && hasContact);
  },
  {
    message: "Must select either an account or a contact (not both)",
    path: ["ownerAccountId"],
  }
);

type SecurityOwnerFormData = z.infer<typeof securityOwnerFormSchema>;

interface SecurityOwnerFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  owner?: any;
  onSuccess?: () => void;
  clientGroupId?: string;
}

const OWNERSHIP_BASIS_OPTIONS = [
  { value: "sole", label: "Sole Ownership" },
  { value: "joint_tenants", label: "Joint Tenants" },
  { value: "tenants_in_common", label: "Tenants in Common" },
];

export function SecurityOwnerFormModal({
  open,
  onOpenChange,
  securityId,
  owner,
  onSuccess,
  clientGroupId,
}: SecurityOwnerFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!owner;

  const form = useForm<SecurityOwnerFormData>({
    resolver: zodResolver(securityOwnerFormSchema),
    values: {
      securityId,
      ownerType: owner?.ownerAccountId ? "account" : "contact",
      ownerAccountId: owner?.ownerAccountId || null,
      ownerContactId: owner?.ownerContactId || null,
      ownershipBasis: owner?.ownershipBasis || "sole",
      ownershipPercentage: owner?.ownershipPercentage || "100",
      isRegisteredProprietor: owner?.isRegisteredProprietor ?? true,
      notes: owner?.notes || "",
    },
  });

  const ownerType = form.watch("ownerType");

  const createMutation = useMutation({
    mutationFn: async (data: SecurityOwnerFormData) => {
      // Validate ownership percentage (0-100)
      const percentage = parseFloat(data.ownershipPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        throw new Error("Ownership percentage must be between 0 and 100");
      }

      const endpoint = isEditing
        ? `/api/crm/securities/${securityId}/owners/${owner.id}`
        : `/api/crm/securities/${securityId}/owners`;
      const method = isEditing ? "PATCH" : "POST";

      // Prepare payload based on owner type
      const payload = {
        ...(isEditing ? {} : { securityId: data.securityId }),
        ownerAccountId: data.ownerType === "account" ? data.ownerAccountId : null,
        ownerContactId: data.ownerType === "contact" ? data.ownerContactId : null,
        ownershipBasis: data.ownershipBasis,
        ownershipPercentage: data.ownershipPercentage,
        isRegisteredProprietor: data.isRegisteredProprietor,
        notes: data.notes,
      };

      const response = await apiRequest(method, endpoint, payload);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to save owner");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/owners`] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}`] });
      toast({
        title: "Success",
        description: `Owner ${isEditing ? "updated" : "added"} successfully`,
      });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SecurityOwnerFormData) => {
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Security Owner</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update" : "Add"} an owner for this security
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="ownerType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Owner Type</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="account" id="account" />
                        <label htmlFor="account" className="text-sm font-medium cursor-pointer">
                          Account/Company
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="contact" id="contact" />
                        <label htmlFor="contact" className="text-sm font-medium cursor-pointer">
                          Individual Contact
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {ownerType === "account" && (
              <FormField
                control={form.control}
                name="ownerAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account</FormLabel>
                    <FormControl>
                      <AccountSelect value={field.value} onValueChange={field.onChange} clientGroupId={clientGroupId} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {ownerType === "contact" && (
              <FormField
                control={form.control}
                name="ownerContactId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact</FormLabel>
                    <FormControl>
                      <ContactSelect value={field.value} onValueChange={field.onChange} clientGroupId={clientGroupId} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="ownershipBasis"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ownership Basis</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select basis" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OWNERSHIP_BASIS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ownershipPercentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ownership %</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="100.00"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Must be between 0-100%</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isRegisteredProprietor"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Registered Proprietor</FormLabel>
                    <FormDescription>
                      Owner is registered on the title deed
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional ownership details..."
                      className="min-h-[60px]"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Update" : "Add"} Owner
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
